/**
 * App Bridge 类型声明与统一调用封装
 * 支持多种 Bridge 对象：nativeObj / AndroidBridge / webkit / AppBridge
 */

declare global {
  interface Window {
    // 主 Bridge 对象（App 端注入）
    nativeObj?: {
      getToken: () => Promise<string>;
      getLanguage: () => Promise<string>;
      getThemeMode?: () => Promise<number>;
      getAvatarUrl?: () => Promise<string>;
      goBack?: () => Promise<void>;
      exitPage?: () => Promise<void>;
      // 后续扩展的方法会自动支持
      [key: string]: (() => Promise<unknown>) | undefined;
    };
    // Android WebView Bridge
    AndroidBridge?: {
      goBack: () => void;
      exitPage?: () => void;
      getToken?: () => Promise<string>;
      getLanguage?: () => Promise<string>;
      getThemeMode?: () => Promise<number>;
      getAvatarUrl?: () => Promise<string>;
      [key: string]: (() => unknown) | undefined;
    };
    // iOS WKWebView Bridge
    webkit?: {
      messageHandlers?: {
        goBack?: { postMessage: (data: unknown) => void };
        exitPage?: { postMessage: (data: unknown) => void };
        getToken?: { postMessage: (data: unknown) => void };
        getLanguage?: { postMessage: (data: unknown) => void };
        getThemeMode?: { postMessage: (data: unknown) => void };
        getAvatarUrl?: { postMessage: (data: unknown) => void };
        [key: string]: { postMessage: (data: unknown) => void } | undefined;
      };
    };
    // 通用 Bridge（App 可自定义名称）
    AppBridge?: {
      goBack: () => void;
      exitPage?: () => void;
      getToken?: () => Promise<string>;
      getLanguage?: () => Promise<string>;
      getThemeMode?: () => Promise<number>;
      getAvatarUrl?: () => Promise<string>;
      [key: string]: (() => unknown) | undefined;
    };
  }
}

/**
 * 检测可用的 Bridge 对象
 * 优先级：nativeObj > AndroidBridge > webkit > AppBridge
 */
function detectBridge(): {
  type: 'nativeObj' | 'AndroidBridge' | 'webkit' | 'AppBridge' | null;
  bridge: unknown;
} {
  if (window.nativeObj) {
    return { type: 'nativeObj', bridge: window.nativeObj };
  }

  if (window.AndroidBridge) {
    return { type: 'AndroidBridge', bridge: window.AndroidBridge };
  }
  if (window.webkit?.messageHandlers) {
    return { type: 'webkit', bridge: window.webkit.messageHandlers };
  }
  if (window.AppBridge) {
    return { type: 'AppBridge', bridge: window.AppBridge };
  }
  return { type: null, bridge: null };
}

/**
 * 轮询等待 Bridge 就绪
 * @param timeout 超时时间（毫秒），默认 15000ms
 * @param interval 轮询间隔（毫秒），默认 50ms
 * @returns Promise<boolean> Bridge 是否就绪
 */
export async function waitForBridge(
  timeout = 15000,
  interval = 50
): Promise<boolean> {
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const { type } = detectBridge();
    if (type) {
      console.log(`[Bridge] Bridge ready: ${type}`);
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  console.log('[Bridge] Bridge wait timeout');
  return false;
}

/**
 * 统一调用原生 Bridge 方法
 * 封装不同平台 Bridge 的调用差异，提供统一的异步接口
 *
 * @param method 方法名（如 getToken, getLanguage, goBack, exitPage）
 * @param args 参数（仅 iOS webkit 需要，其他平台忽略）
 * @returns Promise<string | void> 方法返回值
 * @throws Error 如果 Bridge 未就绪或方法不存在
 */
export async function callNativeMethod(
  method: string,
  args?: unknown
): Promise<string | void> {
  const { type, bridge } = detectBridge();

  if (!type || !bridge) {
    throw new Error(`[Bridge] No bridge available for method '${method}'`);
  }

  switch (type) {
    case 'nativeObj': {
      const nativeBridge = bridge as typeof window.nativeObj;
      if (nativeBridge[method]) {
        return await nativeBridge[method]!();
      }
      throw new Error(`[Bridge] Method '${method}' not found on nativeObj`);
    }

    case 'AndroidBridge': {
      const androidBridge = bridge as typeof window.AndroidBridge;
      if (androidBridge[method]) {
        const result = androidBridge[method]!();
        // Android Bridge 可能是同步或异步，统一处理
        if (result instanceof Promise) {
          return await result;
        }
        return result as string | void;
      }
      throw new Error(`[Bridge] Method '${method}' not found on AndroidBridge`);
    }

    case 'webkit': {
      const handlers = bridge as typeof window.webkit.messageHandlers;
      if (handlers[method]) {
        handlers[method]!.postMessage(args ?? null);
        // iOS webkit postMessage 无返回值，需要通过回调获取
        return;
      }
      throw new Error(`[Bridge] Method '${method}' not found on webkit.messageHandlers`);
    }

    case 'AppBridge': {
      const appBridge = bridge as typeof window.AppBridge;
      if (appBridge[method]) {
        const result = appBridge[method]!();
        if (result instanceof Promise) {
          return await result;
        }
        return result as string | void;
      }
      throw new Error(`[Bridge] Method '${method}' not found on AppBridge`);
    }

    default:
      throw new Error(`[Bridge] Unknown bridge type: ${type}`);
  }
}

/**
 * 检查 Bridge 是否存在指定方法
 * @param method 方法名
 * @returns 方法是否存在
 */
export function hasNativeMethod(method: string): boolean {
  const { type, bridge } = detectBridge();

  if (!type || !bridge) return false;

  switch (type) {
    case 'nativeObj':
      return !!(bridge as typeof window.nativeObj)[method];
    case 'AndroidBridge':
      return !!(bridge as typeof window.AndroidBridge)[method];
    case 'webkit':
      return !!(bridge as typeof window.webkit.messageHandlers)[method];
    case 'AppBridge':
      return !!(bridge as typeof window.AppBridge)[method];
    default:
      return false;
  }
}

/**
 * 判断是否在 WebView 环境中
 */
export function isInWebView(): boolean {
  return detectBridge().type !== null;
}

/**
 * 调用 App 返回函数
 * WebView 模式下点击返回按钮时调用
 *
 * 调用优先级：
 * 1. exitPage（语义最明确：退出当前页）
 * 2. goBack（返回上一页）
 * 3. Fallback: window.history.back()
 */
export async function callAppGoBack(): Promise<void> {
  // 优先尝试 exitPage
  if (hasNativeMethod('exitPage')) {
    await callNativeMethod('exitPage');
    return;
  }

  // 其次尝试 goBack
  if (hasNativeMethod('goBack')) {
    await callNativeMethod('goBack');
    return;
  }

  // Fallback: 浏览器历史返回
  window.history.back();
}

/**
 * 调试工具：扫描并打印 window 上所有 Bridge 相关对象
 * 用于开发环境排查 Bridge 注入问题
 */
export function debugScanBridge(): void {
  console.log('[Bridge] === Scanning Bridge Objects ===');

  const bridgeNames = ['nativeObj', 'AndroidBridge', 'webkit', 'AppBridge'];

  bridgeNames.forEach((name) => {
    const obj = (window as Record<string, unknown>)[name];
    if (obj) {
      console.log(`[Bridge] Found ${name}:`, obj);
      console.log(`[Bridge] ${name} methods:`, Object.keys(obj));
    } else {
      console.log(`[Bridge] ${name}: not found`);
    }
  });

  const { type } = detectBridge();
  console.log(`[Bridge] Detected bridge type: ${type || 'none'}`);
}

// 确保 global 声明被导出（空导出使文件成为模块）
export {};